import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Mail, MessageCircle, Home, User, Calendar, Building2, Menu, X, ShoppingBag } from "lucide-react";
import { TpcoLogo } from "../site/TpcoLogo";
import SEOHead, { ORG_JSONLD } from "../site/SEOHead";
import CookieConsent from "../site/CookieConsent";
import AnalyticsLoader from "../site/AnalyticsLoader";
import { CONTACT_PHONE as PHONE, waLink } from "../../config/contact";

const WA = waLink("Bonjour TPCO Senegal,");

export default function VenteLayout({ children }) {
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isHome = loc.pathname === "/";

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col" style={{ fontFamily: "Inter, system-ui, sans-serif" }}>
      <SEOHead title="Catalogue de biens immobiliers à vendre" description="Découvrez tous les biens TPCO Sénégal à vendre : appartements, maisons, lots, duplex à Dakar. Filtrez par localisation, prix, chambres et surface." />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ORG_JSONLD) }} />

      {/* Top utility bar — liens vers les autres sites */}
      <div className="bg-white border-b border-[#E2E5F1]">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-10 h-12 flex items-center justify-between gap-2">
          <div className="hidden md:flex items-center gap-1 text-[12px]">
            <a href="https://tpco.sn" className="inline-flex items-center gap-1.5 px-3 h-10 rounded hover:bg-[#FCE4EC] transition" data-testid="vente-util-site">
              <Home className="w-4 h-4 text-[#E91E63]" />
              <span className="font-semibold text-[#0F1A3D]">Site TPCO</span>
            </a>
            <a href="https://tpco.sn/projets" className="inline-flex items-center gap-1.5 px-3 h-10 rounded hover:bg-[#FCE4EC] transition" data-testid="vente-util-projets">
              <Building2 className="w-4 h-4 text-[#E91E63]" />
              <span className="font-semibold text-[#0F1A3D]">Nos projets</span>
            </a>
            <a href="https://tpco.sn/contact" className="inline-flex items-center gap-1.5 px-3 h-10 rounded hover:bg-[#FCE4EC] transition" data-testid="vente-util-rdv">
              <Calendar className="w-4 h-4 text-[#E91E63]" />
              <span className="font-semibold text-[#0F1A3D]">Prendre rendez-vous</span>
            </a>
            <a href="https://monespace.tpco.sn" className="inline-flex items-center gap-1.5 px-3 h-10 rounded hover:bg-[#FCE4EC] transition" data-testid="vente-util-espace">
              <User className="w-4 h-4 text-[#E91E63]" />
              <span className="font-semibold text-[#0F1A3D]">Espace client</span>
            </a>
          </div>
          <div className="md:hidden flex items-center gap-2 text-[11px] text-[#4B5775]">
            <ShoppingBag className="w-3.5 h-3.5 text-[#E91E63]" />
            <span className="font-bold">Catalogue immobilier</span>
          </div>
          <div className="flex items-center gap-2">
            <a href={`tel:${PHONE.replace(/\s/g, "")}`} className="hidden sm:inline-flex items-center gap-2 h-10 px-3 bg-[#E5F8E9] hover:bg-[#D4F0DC] border border-[#A8DCB4] rounded transition">
              <Phone className="w-3.5 h-3.5 text-[#2D7A3F]" />
              <div className="flex flex-col leading-tight">
                <span className="font-extrabold text-[12px] text-[#0F1A3D]">{PHONE}</span>
                <span className="text-[8px] uppercase tracking-wider text-[#2D7A3F] font-bold">Service & appel gratuits</span>
              </div>
            </a>
            <a href={WA} target="_blank" rel="noopener noreferrer" className="sm:hidden h-9 w-9 rounded bg-[#25D366] text-white flex items-center justify-center" aria-label="WhatsApp">
              <MessageCircle className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>

      {/* Header principal */}
      <header className="sticky top-0 z-30 bg-white shadow-sm">
        <div className="max-w-[1400px] mx-auto pl-4 lg:pl-10 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 py-3" data-testid="vente-logo">
            <TpcoLogo variant="light" size="lg" />
          </Link>
          <nav className="hidden lg:flex items-center">
            <Link
              to="/"
              className={`px-4 py-7 text-[13px] font-bold uppercase tracking-wider transition ${isHome ? "text-[#E91E63] border-b-2 border-[#E91E63]" : "text-[#0F1A3D] hover:text-[#E91E63] border-b-2 border-transparent"}`}
              data-testid="vente-nav-catalogue"
            >
              Catalogue
            </Link>
            <a href="https://tpco.sn/projets" className="px-4 py-7 text-[13px] font-bold uppercase tracking-wider text-[#0F1A3D] hover:text-[#1E3A8A]" data-testid="vente-nav-projets">
              Nos projets neufs
            </a>
            <a href="https://tpco.sn/qui-sommes-nous" className="px-4 py-7 text-[13px] font-bold uppercase tracking-wider text-[#0F1A3D] hover:text-[#1E3A8A]" data-testid="vente-nav-quisommes">
              Qui sommes-nous
            </a>
            <a href="https://tpco.sn/contact" className="px-4 py-7 text-[13px] font-bold uppercase tracking-wider text-[#0F1A3D] hover:text-[#1E3A8A]" data-testid="vente-nav-contact">
              Contact
            </a>
            <a href="https://tpco.sn/contact" className="ml-3 inline-flex items-center gap-2 h-10 px-4 rounded-md bg-[#E91E63] hover:bg-[#D81B60] text-white text-[12px] font-bold uppercase tracking-wider transition">
              <Calendar className="w-3.5 h-3.5" /> Demander un devis
            </a>
          </nav>
          <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden p-4" aria-label="Menu" data-testid="vente-mobile-menu-btn">
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {mobileOpen && (
          <nav className="lg:hidden border-t border-[#E2E5F1] bg-white" data-testid="vente-mobile-nav">
            <Link to="/" className={`block px-6 py-3 text-sm font-bold uppercase tracking-wider border-b border-[#E2E5F1] ${isHome ? "text-[#E91E63] bg-[#FCE4EC]" : "text-[#0F1A3D]"}`}>Catalogue</Link>
            <a href="https://tpco.sn/projets" className="block px-6 py-3 text-sm font-bold uppercase tracking-wider border-b border-[#E2E5F1] text-[#0F1A3D]">Nos projets neufs</a>
            <a href="https://tpco.sn/qui-sommes-nous" className="block px-6 py-3 text-sm font-bold uppercase tracking-wider border-b border-[#E2E5F1] text-[#0F1A3D]">Qui sommes-nous</a>
            <a href="https://tpco.sn/contact" className="block px-6 py-3 text-sm font-bold uppercase tracking-wider border-b border-[#E2E5F1] text-[#0F1A3D]">Contact</a>
            <a href="https://monespace.tpco.sn" className="block px-6 py-3 text-sm font-bold uppercase tracking-wider border-b border-[#E2E5F1] text-[#E91E63]">Espace client →</a>
            <a href="https://tpco.sn" className="block px-6 py-4 text-sm font-bold uppercase tracking-wider bg-[#1E3A8A] text-white text-center">Retour au site TPCO</a>
          </nav>
        )}
      </header>

      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-[#0F1A3D] text-white mt-16">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <TpcoLogo variant="dark" size="md" className="mb-4" />
              <p className="text-xs text-white/70 leading-relaxed mt-3">Construire autrement et mieux. TPCO Sénégal — Touba Pro Construction.</p>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#E91E63] font-bold mb-3">Nos sites</div>
              <ul className="space-y-2 text-xs text-white/80">
                <li><a href="https://tpco.sn" className="hover:text-white">Site institutionnel</a></li>
                <li><a href="/" className="hover:text-white">Catalogue de biens</a></li>
                <li><a href="https://monespace.tpco.sn" className="hover:text-white">Espace client</a></li>
              </ul>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#E91E63] font-bold mb-3">Contact</div>
              <ul className="space-y-2 text-xs text-white/80">
                <li className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" /> {PHONE}</li>
                <li className="flex items-center gap-2"><Mail className="w-3.5 h-3.5" /> contact@tpco.sn</li>
              </ul>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#E91E63] font-bold mb-3">Suivez-nous</div>
              <a href={WA} className="inline-flex items-center gap-2 h-9 px-4 rounded-md bg-[#25D366] hover:bg-[#1FB855] text-white text-xs font-semibold">
                <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
              </a>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 text-[11px] text-white/50">
            © {new Date().getFullYear()} TPCO Sénégal · Tous droits réservés
          </div>
        </div>
      </footer>

      {/* Bouton WhatsApp flottant */}
      <a href={WA} target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-5 z-40 w-14 h-14 bg-[#25D366] hover:bg-[#1FB855] text-white rounded-full shadow-lg flex items-center justify-center transition" aria-label="WhatsApp" data-testid="vente-wa-fab">
        <MessageCircle className="w-6 h-6" />
      </a>

      <AnalyticsLoader />
      <CookieConsent />
    </div>
  );
}
